import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";

const statuses = [
  { value: "pending", label: "Pågår" },
  { value: "completed", label: "Fullført" },
];

export default function MilestoneForm({ milestone, projectId, projectName, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    due_date: "",
    status: "pending",
    completed_date: "",
  });

  useEffect(() => {
    if (milestone) {
      setFormData({
        name: milestone.name || "",
        description: milestone.description || "",
        due_date: milestone.due_date || "",
        status: milestone.status || "pending",
        completed_date: milestone.completed_date || "",
      });
    }
  }, [milestone]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      project_id: projectId,
      project_name: projectName,
    };
    onSubmit(data);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {milestone ? "Rediger Milepæl" : "Opprett Milepæl"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Milepælnavn *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="f.eks. MVP Lansering"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Beskrivelse</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Hva skal oppnås ved denne milepælen?"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="due_date">Forfallsdato *</Label>
            <Input
              id="due_date"
              type="date"
              value={formData.due_date}
              onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {statuses.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {formData.status === "completed" && (
            <div className="space-y-2">
              <Label htmlFor="completed_date">Fullført dato</Label>
              <Input
                id="completed_date"
                type="date"
                value={formData.completed_date}
                onChange={(e) => setFormData({ ...formData, completed_date: e.target.value })}
              />
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Avbryt</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Lagrer..." : milestone ? "Oppdater" : "Opprett"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}